import RoomLoginForm from 'features/room/components/RoomLoginForm';
import React, { useCallback } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { authorizeUser } from 'features/room/thunks/authorizeUser';
import { setConsentFlag } from 'utils/consentFlag';
import { eventBus } from 'utils/eventBus';
import * as Sentry from '@sentry/react';
import { handleAsyncFormValidation } from 'utils/handleAsyncFormValidation';
import { getAPIDomain, lookupApiErrorMessage } from 'utils/http';
import { logger } from 'utils/logger';
import { isAPIError } from 'utils/types';
import {
  loginFailed,
  loginStarted,
  selectLoginError,
  selectRoomEntryProgress,
} from 'features/room/roomSlice';
import { signalingUserBanned } from 'features/users/actions';

export interface RoomLoginFormValues {
  displayName: string;
  consentGiven?: boolean;
}

const RoomLogin = () => {
  const { t } = useTranslation(['join', 'common']);
  const dispatch = useAppDispatch();

  const entryProgress = useAppSelector(selectRoomEntryProgress);

  const loginError = useAppSelector(selectLoginError);

  const {
    handleSubmit,
    setError,
    formState: { errors },
    control,
  } = useForm<RoomLoginFormValues>({
    defaultValues: {
      displayName: eventBus.initState.username || '',
    },
    errors: loginError
      ? {
          displayName: { type: 'value', message: loginError },
        }
      : undefined,
  });

  const submitLogin: SubmitHandler<RoomLoginFormValues> = useCallback(
    async (data) => {
      dispatch(loginStarted());

      if (data.consentGiven) {
        logger.remote({ tier: 1 }).log('User accepted GDPR terms for team: ', getAPIDomain());

        setConsentFlag();
      }

      const resultAction = await dispatch(authorizeUser(data.displayName));

      if (authorizeUser.rejected.match(resultAction)) {
        dispatch(loginFailed());

        if (resultAction.payload) {
          handleAsyncFormValidation(resultAction.payload, setError);
          return;
        }

        const { error } = resultAction;
        const apiError = isAPIError(error) ? error : undefined;
        const errorMessage = apiError ? lookupApiErrorMessage(apiError)?.description : undefined;

        if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
          logger
            .remote({ tier: 1 })
            .warn('Login prejoin request failed to reach server:', error.code);
        } else if (errorMessage) {
          // a rejection we can explain to the user is a server outcome, not an app defect
          logger.remote({ tier: 1 }).warn('Login rejected by server:', apiError?.type);
        } else {
          Sentry.captureException(
            new Error(`Login authorization failed: ${error.message ?? 'unknown error'}`, {
              cause: error,
            })
          );
        }

        if (apiError?.type === 'banned') {
          dispatch(signalingUserBanned(apiError.details));
          return;
        }

        setError('root.serverError', {
          type: 'server',
          message: errorMessage || t('common:generic_error'),
        });
      }
    },
    [dispatch, setError, t]
  );

  return (
    <RoomLoginForm
      onSubmit={handleSubmit(submitLogin)}
      errors={errors}
      control={control}
      isSubmitting={entryProgress === 'authorizing'}
    />
  );
};

export default RoomLogin;
